import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ResponseError } from '../models/response-error';
import { JwtResponseError } from '@devlearning/jwt-auth';
import { throwError, type Observable } from 'rxjs';
import moment from 'moment-timezone';
import { ApiAutogeneratedService } from '../autogenerated/api.autogenerated';
import { ApiErrorNotifierService } from './api-error-notifier.service';

/**
 * Enhanced API service with custom serialization, error handling, and timezone support.
 * Extends the auto-generated API service with additional functionality for:
 * - Moment.js date/time serialization with timezone support
 * - Deep object transformation for requests/responses
 * - Event-based error notification (decoupled from UI)
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService extends ApiAutogeneratedService {

  // Regex per riconoscere i formati
  // 1) ISO istante: 2025-09-23T14:05:12.345Z  oppure  +02:00 / -05:00
  private readonly ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}(Z|[+-]\d{2}:\d{2})$/;

  // 2) ISO locale con IANA tra []: 2025-10-26T10:00:00.000[Europe/Rome]
  private readonly ISO_LOCAL_IANA = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}\[([A-Za-z_\/]+)\]$/;

  // 3) time-only: 10:00:00.000 (lasciamo stringa)
  private readonly TIME_ONLY = /^\d{2}:\d{2}:\d{2}\.\d{3}$/;

  // Stati HTTP che non richiedono notifica all'utente
  private readonly SILENT_ERROR_CODES = new Set([
    HttpStatusCode.Unauthorized,
    HttpStatusCode.Forbidden,
  ]);

  constructor(
    public override _http: HttpClient,
    private readonly _errorNotifier: ApiErrorNotifierService,
  ) {
    super(_http, environment.BASE_URL);
  }

  protected override _momentToString(moment: moment.Moment): string {
    const m = moment.clone();

    const tzName = (m as any).tz ? (m as any).tz() : undefined;

    // Se ha una zona IANA esplicita (diversa da 'UTC'), serializziamo come ISO + [IANA]
    // if (tzName && tzName !== 'UTC') {
    //   return `${m.format("YYYY-MM-DD'T'HH:mm:ss.SSS")}[${tzName}]`;
    // }

    // Altrimenti forziamo UTC e serializziamo Zulu
    return m.utc().toISOString(); // es. 2025-09-23T14:05:12.345Z
  }

  /**
   * Handles multipart/form-data requests for file uploads.
   * Converts request objects to FormData, handling Files and primitive types.
   */
  protected override _handleMultipart<T>(request: T): FormData {
    const formData = new FormData();

    if (request === null || request === undefined) {
      return formData;
    }

    // Itera sulle proprietà dell'oggetto request
    for (const key of Object.keys(request as object)) {
      const value = (request as any)[key];

      if (value === null || value === undefined) {
        continue; // Skip null/undefined values
      }

      if (value instanceof File) {
        // File upload
        formData.append(key, value, value.name);
      } else if (value instanceof Blob) {
        // Blob upload
        formData.append(key, value);
      } else if (Array.isArray(value)) {
        // Array handling
        value.forEach((item, index) => {
          if (item instanceof File) {
            formData.append(`${key}[${index}]`, item, item.name);
          } else {
            formData.append(`${key}[${index}]`, String(item));
          }
        });
      } else if (moment.isMoment(value)) {
        // Moment serialization
        formData.append(key, this._momentToString(value));
      } else if (value instanceof Date) {
        // Date serialization
        formData.append(key, moment(value).toISOString());
      } else if (typeof value === 'object') {
        // Complex objects -> JSON string
        formData.append(key, JSON.stringify(value));
      } else {
        // Primitive types
        formData.append(key, String(value));
      }
    }

    return formData;
  }

  // Serializza in profondità:
  // - moment UTC  -> ISO Zulu
  // - moment TZ   -> ISO + [IANA]
  // - Date native -> ISO Zulu
  protected override _handleRequest<T>(request: T): T {
    return this._mapDeep(request, (val) => {
      if (moment.isMoment(val)) {
        const m = (val as moment.Moment).clone();
        return m.format("YYYY-MM-DDTHH:mm:ss.SSS"); // es. 2025-09-23T14:05:12.345Z
      }
      // Date native -> ISO Zulu
      if (val instanceof Date) {
        return moment(val).format("YYYY-MM-DDTHH:mm:ss.SSS"); // es. 2025-09-23T14:05:12.345Z
      }

      return val;
    });
  }

  /** ===================== PARSING RESPONSE ===================== */

  // Parse in profondità:
  // - "....Z" o "....+02:00" -> moment UTC (istante)
  // - "....[Europe/Rome]"    -> moment.tz(..., IANA) (locale con zona)
  // - "HH:mm:ss.SSS"         -> lasciamo stringa (time-only)
  protected override _handleResponse<T>(response: T): T {
    return this._mapDeep(response, (val) => {
      if (typeof val !== 'string') return val;

      // ISTANTE UTC/offset
      if (this.ISO_INSTANT.test(val)) {
        // parseZone rispetta l'offset della stringa; .utc(true) mantiene lo stesso istante
        return moment.parseZone(val).utc(true);
      }

      // TIME-ONLY -> stringa
      if (this.TIME_ONLY.test(val)) {
        return val;
      }

      // Altro: lascia com'è
      return val;
    });
  }

  /** ===================== UTILITY COMUNI ===================== */

  // Mapper profondo immutabile per oggetti/array/moment
  private _mapDeep<T>(input: T, f: (value: any) => any): T {
    if (input === null || input === undefined) return input;

    // moment viene trattato come foglia
    if (moment.isMoment(input)) return f(input);

    // tipi primitivi
    if (typeof input !== 'object') return f(input);

    // Array
    if (Array.isArray(input)) {
      const arr = input.map(item => this._mapDeep(item, f));
      return f(arr) as unknown as T;
    }

    // Oggetto
    const obj: any = { ...(input as any) };
    for (const k of Object.keys(obj)) {
      obj[k] = this._mapDeep(obj[k], f);
    }
    return f(obj) as T;
  }

  /**
   * Enhanced error handler with structured error processing and event-based notification.
   *
   * @param error - The error object from HTTP request
   * @param obs - Observable context (unused but required by base class)
   * @param skipErrorHandling - If true, skips error notification event emission
   * @returns Observable that throws a ResponseError
   */
  protected override _handleError<T>(
    error: any,
    obs: Observable<T>,
    skipErrorHandling: boolean = false
  ): Observable<never> {
    const responseError = this._buildResponseError(error);
    const errorType = this._getErrorType(error);
    const shouldNotify = !skipErrorHandling && this._shouldNotifyUser(error);

    // Log strutturato per debugging
    this._logError(error, responseError);

    // Emette evento per subscribers (UI handler, monitoring, etc.)
    const errorContext = this._errorNotifier.createContext(
      responseError,
      errorType,
      shouldNotify,
      error?.url,
      error?.status
    );

    this._errorNotifier.notifyError(errorContext);

    return throwError(() => responseError);
  }

  /**
   * Costruisce un ResponseError strutturato dal tipo di errore ricevuto.
   */
  private _buildResponseError(error: any): ResponseError {
    const responseError = new ResponseError();

    // 1. Client-side error (network, parsing, etc.)
    if (error.error instanceof Error) {
      responseError.message = error.error.message;
      responseError.stacktrace = error.error.stack;
      return responseError;
    }

    // 2. JWT Authentication error
    if (error instanceof JwtResponseError) {
      responseError.message = error.isUnhautorized
        ? 'Session expired. Please login again.'
        : error.message;
      responseError.stacktrace = error.detailedMessage;
      return responseError;
    }

    // 3. HTTP error response
    if (error instanceof HttpErrorResponse) {
      return ResponseError.CreateFromHttpErrorResponse(error);
    }

    // 4. Unknown error type
    responseError.message = error?.message || 'An unexpected error occurred';
    return responseError;
  }

  /**
   * Determina se l'errore deve essere notificato all'utente.
   * Alcuni errori (401, 403) sono gestiti a livello globale e non richiedono notifica.
   */
  private _shouldNotifyUser(error: any): boolean {
    if (error instanceof HttpErrorResponse) {
      return !this.SILENT_ERROR_CODES.has(error.status);
    }

    if (error instanceof JwtResponseError && error.isUnhautorized) {
      return false; // L'auth handler mostrerà la pagina di login
    }

    return true;
  }

  /**
   * Logging strutturato degli errori per debugging e monitoraggio.
   */
  private _logError(originalError: any, responseError: ResponseError): void {
    const errorContext = {
      timestamp: new Date().toISOString(),
      statusCode: originalError?.status,
      url: originalError?.url,
      message: responseError.message,
      stacktrace: responseError.stacktrace,
      errorType: this._getErrorType(originalError),
    };

    // In produzione, potresti inviare questo a un servizio di logging (Sentry, AppInsights, etc.)
    if (environment.production) {
      console.error('[API Error]', errorContext);
      // TODO: Invia a servizio di monitoring esterno
      // this._monitoring.logError(errorContext);
    } else {
      // In development, log dettagliato per debugging
      console.group('🔴 API Error');
      console.error('Status:', errorContext.statusCode);
      console.error('URL:', errorContext.url);
      console.error('Message:', errorContext.message);
      if (errorContext.stacktrace) {
        console.error('Stacktrace:', errorContext.stacktrace);
      }
      console.error('Original Error:', originalError);
      console.groupEnd();
    }
  }

  /**
   * Determina il tipo di errore per categorizzazione.
   */
  private _getErrorType(error: any): string {
    if (error.error instanceof Error) return 'CLIENT_ERROR';
    if (error instanceof JwtResponseError) return 'AUTH_ERROR';
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) return 'NETWORK_ERROR';
      if (error.status >= 400 && error.status < 500) return 'CLIENT_HTTP_ERROR';
      if (error.status >= 500) return 'SERVER_ERROR';
      return 'HTTP_ERROR';
    }
    return 'UNKNOWN_ERROR';
  }
}
